import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { YandexMap } from '../../components/YandexMap';
import './RouletteGamePage.css';

interface RouletteEntry {
  id: string;
  plateNumber: string;
  latitude: number;
  longitude: number;
  is_correct: boolean;
  is_duplicate: boolean;
  fraud_warning?: string;
  created_at: string;
}

interface GameData {
  id: string;
  gameType: string;
  status: string;
  rouletteInfo: {
    region_code: number;
    target_digits: number;
    is_completed: boolean;
  };
  entries: RouletteEntry[];
}

export function RouletteGamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameData | null>(null);
  const [loading, setLoading] = useState(true);
  const [plateNumber, setPlateNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number; accuracy?: number } | null>(null);

  useEffect(() => {
    loadGame();
    getLocation();
    
    // Сохраняем текущую игру как последнюю
    if (gameId) {
      localStorage.setItem('last-game', JSON.stringify({ gameId, gameType: 'roulette' }));
    }
  }, [gameId]);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Не удалось получить геолокацию');
        }
      );
    } else {
      alert('Геолокация не поддерживается');
    }
  };

  const loadGame = async () => {
    try {
      const response = await api.get(`/games/${gameId}`);
      setGame(response.data);
    } catch (error) {
      console.error('Failed to load game:', error);
      alert('Ошибка загрузки игры');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!plateNumber || !location) {
      alert('Введите номер и разрешите геолокацию');
      return;
    }

    const plateRegex = /^[АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2}\d{2,3}$/;
    if (!plateRegex.test(plateNumber.toUpperCase())) {
      alert('Неверный формат номера. Формат: БЦЦЦББРР (например: А123БВ77)');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/games/${gameId}/roulette/submit`, {
        plateNumber: plateNumber.toUpperCase(),
        latitude: location.lat,
        longitude: location.lng,
        accuracy: location.accuracy,
      });

      if (response.data.fraudWarning) {
        alert(`Предупреждение: ${response.data.fraudWarning.join(', ')}`);
      }

      if (response.data.isCorrect) {
        alert('🎉 Правильно! Игра завершена!');
        setPlateNumber('');
      } else if (response.data.isDuplicate) {
        alert('Этот номер уже был введён');
      } else {
        alert('Неправильный номер. Попробуйте ещё раз!');
      }

      await loadGame();
    } catch (error: any) {
      // Обрабатываем офлайн-режим
      if (error.response?.data?.offline || error.isOffline) {
        alert('Запрос сохранен. Будет отправлен при восстановлении соединения.');
        // Обновляем локально для отображения
        if (game) {
          const newEntry = {
            id: `offline-${Date.now()}`,
            plateNumber: plateNumber.toUpperCase(),
            latitude: location.lat,
            longitude: location.lng,
            is_correct: false, // Будет определено при синхронизации
            is_duplicate: false,
            created_at: new Date().toISOString(),
          };
          setGame({
            ...game,
            entries: [newEntry, ...game.entries],
          });
        }
        setPlateNumber('');
        getLocation();
      } else {
        alert(error.response?.data?.error?.message || 'Ошибка');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (!game) {
    return <div>Игра не найдена</div>;
  }

  const { rouletteInfo, entries } = game;
  const isCompleted = rouletteInfo.is_completed;

  return (
    <div className="roulette-game-page">
      <div className="game-header">
        <button className="back-button" onClick={() => {
          // Устанавливаем флаг, что пользователь явно вышел на главную
          localStorage.setItem('skip-auto-redirect', 'true');
          navigate('/');
        }}>
          ← Назад
        </button>
        <h1>Рулетка: Регион {String(rouletteInfo.region_code).padStart(2, '0')}</h1>
      </div>

      <div className="game-info">
        <div className="target-info">
          <h3>Целевые цифры:</h3>
          <div className="target-digits">
            {String(rouletteInfo.target_digits).padStart(3, '0')}
          </div>
        </div>
        {isCompleted && (
          <div className="completed-badge">✓ Игра завершена!</div>
        )}
      </div>

      {!isCompleted && (
        <div className="input-section">
          <input
            type="text"
            placeholder="Введите номер (например: А123БВ77)"
            value={plateNumber}
            onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
            className="plate-input"
            maxLength={9}
          />
          <button
            onClick={handleSubmit}
            disabled={submitting || !location}
            className="submit-button"
          >
            {submitting ? 'Отправка...' : 'Отправить'}
          </button>
          {!location && (
            <p className="location-warning">Ожидание геолокации...</p>
          )}
        </div>
      )}

      <div className="map-container">
        <YandexMap
          center={entries.length > 0 
            ? [entries[0].latitude, entries[0].longitude]
            : [55.7558, 37.6173]}
          zoom={10}
          markers={entries.map((entry) => ({
            id: entry.id,
            position: [entry.latitude, entry.longitude],
            content: `
              <div>
                <strong>${entry.plateNumber}</strong><br/>
                ${entry.is_correct ? '✓ Правильно!' : '✗ Неправильно'}
                ${entry.fraud_warning ? `<br/><span style="color: orange;">⚠ ${entry.fraud_warning}</span>` : ''}
              </div>
            `,
            color: entry.is_correct ? 'green' : 'red',
          }))}
        />
      </div>

      <div className="entries-list">
        <h3>Попытки</h3>
        {entries.length === 0 ? (
          <p>Нет попыток</p>
        ) : (
          <div className="entries">
            {entries.map((entry) => (
              <div key={entry.id} className="entry">
                <span className={`plate-number ${entry.is_correct ? 'correct' : 'incorrect'}`}>
                  {entry.plateNumber}
                </span>
                <span className="entry-time">
                  {new Date(entry.created_at).toLocaleString('ru-RU')}
                </span>
                {entry.fraud_warning && (
                  <span className="fraud-warning">⚠</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

